"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

const DESKTOP_QUERY = "(min-width: 1024px)";

function subscribeDesktop(cb: () => void) {
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

/** `true` on the pinned desktop layout; `false` on the server and on phones/tablets. */
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeDesktop,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => false,
  );
}

/**
 * How far (px) a pinned section has been scrolled since its sticky panel started
 * sticking: negative while the panel is still arriving. rAF-throttled passive listener;
 * the sticky offset is read from the panel's computed `top`, so CSS tweaks never desync.
 *
 * @param enabled - Only listens while the pinned layout is on screen.
 * @returns Refs for the tall section and its sticky panel, the offset and the sticky top.
 */
export function usePinnedOffset<S extends HTMLElement, P extends HTMLElement>(enabled: boolean) {
  const sectionRef = useRef<S | null>(null);
  const panelRef = useRef<P | null>(null);
  const [state, setState] = useState({ offset: -1, stickyTop: 0 });

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const section = sectionRef.current;
      const panel = panelRef.current;
      if (!section || !panel) return;
      const stickyTop = parseFloat(getComputedStyle(panel).top) || 0;
      const offset = stickyTop - section.getBoundingClientRect().top;
      setState((prev) =>
        Math.abs(prev.offset - offset) < 0.5 && prev.stickyTop === stickyTop ? prev : { offset, stickyTop },
      );
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return { sectionRef, panelRef, ...state };
}
